/**
 * Single source of truth for where the auth session (the JWT **access** token
 * and the user identity/roles) is persisted on the client.
 *
 * The **refresh token is NOT stored here** (or anywhere JS-readable): the
 * backend delivers it as an HttpOnly + Secure + SameSite cookie that the browser
 * holds and JavaScript cannot read, which removes the XSS-theft vector. Only the
 * short-lived access token lives client-side, for the `Authorization: Bearer`
 * header. Centralising read/write/clear here keeps it one place to wipe on
 * logout and one place to change if the access token moves to memory-only.
 */
const SESSION_KEY = "user";

export interface AuthSession {
  userName?: string;
  roles?: string[];
  token?: string;
  issueDate?: string;
  expirationDate?: string;
  preferredLanguage?: string;
  [key: string]: unknown;
}

/** Read the persisted session, tolerating absent/corrupt data. */
const read = (): AuthSession | null => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as AuthSession) : null;
  } catch {
    return null;
  }
};

/** Persist the session (access + refresh tokens, identity, roles). */
const save = (session: AuthSession): void => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
};

/** Remove the persisted session, the only way to "log out" client-side. */
const clear = (): void => {
  localStorage.removeItem(SESSION_KEY);
};

const getAccessToken = (): string | null => read()?.token ?? null;

const authStorage = { read, save, clear, getAccessToken };

export default authStorage;
